export type DocumentViewMode = "formatted" | "source";

export type DocumentSessionCommand = "save" | "edit";

export function isDocumentDirty(
  draft: string,
  committedBody: string | undefined,
): boolean {
  return committedBody !== undefined && draft !== committedBody;
}

export function shouldApplyRemoteDocumentBody(input: {
  readonly draft: string;
  readonly appliedBody: string | undefined;
}): boolean {
  return input.appliedBody === undefined || input.draft === input.appliedBody;
}

export function snapshotMissingOpenFile(
  files: readonly { entry_id: string }[] | undefined,
  openFileId: string | undefined,
): boolean {
  if (openFileId === undefined || openFileId.length === 0) {
    return false;
  }
  if (files === undefined) {
    return false;
  }
  return !files.some((file) => file.entry_id === openFileId);
}

export function confirmDiscardIfDirty(
  dirty: boolean,
  confirmDiscard: () => boolean,
): boolean {
  return !dirty || confirmDiscard();
}

export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return (
    target.closest(
      "input, textarea, select, [contenteditable='true'], .cm-editor, .cm-content, [role='textbox']",
    ) !== null
  );
}

export function isDialogKeyboardTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element && target.closest("[role='dialog']") !== null
  );
}

export function documentSessionCommand(
  event: KeyboardEvent,
): DocumentSessionCommand | null {
  if (event.isComposing || event.altKey || event.shiftKey) {
    return null;
  }
  if (!(event.metaKey || event.ctrlKey)) {
    return null;
  }
  const key = event.key.toLowerCase();
  if (key === "s") {
    return "save";
  }
  if (key === "e") {
    return "edit";
  }
  return null;
}

export function shortcutLabel(key: string): string {
  const mac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/u.test(navigator.platform);
  const glyph = key.length === 1 ? key.toUpperCase() : key;
  return mac ? `⌘${glyph}` : `Ctrl+${glyph}`;
}

export function paneScrollTopDelta(
  key: string,
  viewportHeight: number,
  scrollHeight: number,
  currentTop: number,
): number | null {
  if (key === "ArrowDown") {
    return Math.min(
      currentTop + 48,
      Math.max(0, scrollHeight - viewportHeight),
    );
  }
  if (key === "ArrowUp") {
    return Math.max(0, currentTop - 48);
  }
  if (key === "PageDown" || key === " ") {
    return Math.min(
      currentTop + Math.round(viewportHeight * 0.9),
      Math.max(0, scrollHeight - viewportHeight),
    );
  }
  if (key === "PageUp") {
    return Math.max(0, currentTop - Math.round(viewportHeight * 0.9));
  }
  if (key === "Home") {
    return 0;
  }
  if (key === "End") {
    return Math.max(0, scrollHeight - viewportHeight);
  }
  return null;
}
